import type { SortState } from '../hooks/useSortable'

interface Props {
  col: string
  label: string
  sort: SortState
  onSort: (col: string) => void
  className?: string
  numeric?: boolean   // right-align the header for numeric columns
}

/**
 * SortableHeader — drop-in replacement for <th> in any data table.
 *
 * Usage:
 *   <SortableHeader col="customer_name" label="Customer" sort={sort} onSort={onSort} />
 *   <SortableHeader col="rebill_amount" label="Amount"   sort={sort} onSort={onSort} numeric />
 */
export function SortableHeader({ col, label, sort, onSort, className = '', numeric = false }: Props) {
  const active = sort.col === col
  return (
    <th
      onClick={() => onSort(col)}
      className={`
        cursor-pointer select-none whitespace-nowrap
        text-[10px] font-semibold text-ink-400 uppercase tracking-wide
        py-2 px-2 hover:text-ink-700 transition-colors
        ${numeric ? 'text-right' : 'text-left'}
        ${active ? 'text-ink-700' : ''}
        ${className}
      `}
    >
      <span className="inline-flex items-center gap-1">
        {!numeric && label}
        <span className={`inline-flex flex-col leading-[0] ${active ? 'text-ink-800' : 'text-ink-300'}`}>
          {active ? (
            sort.dir === 'asc'
              ? <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="18 15 12 9 6 15"/></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-2.5 h-2.5 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="18 9 12 3 6 9"/>
              <polyline points="6 15 12 21 18 15"/>
            </svg>
          )}
        </span>
        {numeric && label}
      </span>
    </th>
  )
}
